import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Video, X } from "lucide-react";
import { ImageSelectDialog } from "@/components/ImageSelectDialog";
import { ProductSelectItems } from "@/components/ProductSelectItems";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import type { ProductCatalogOption } from "@/types/ProductCatalog";
import type { SellAProductTemplateContext } from "./types";

type ProductSource = "existing" | "new";

type ProductMedia = {
  fileId: string;
  url: string;
  video?: boolean;
};

export type SellAProductTemplatePayload = {
  productId?: string;
  product?: {
    name: string;
    price: number;
    payWhatYouCan: boolean;
    media?: ProductMedia[];
  };
  countdownText?: string;
  countdownEndDate?: string;
  description?: string;
};

export type SellAProductTemplateFieldsHandle = {
  getPayload: () => SellAProductTemplatePayload;
  isValid: () => boolean;
};

function parsePriceDollarsToCents({ value }: { value: string }): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number.parseFloat(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 100);
}

function centsToDollarsInput({ cents }: { cents?: number }): string {
  if (typeof cents !== "number" || !Number.isFinite(cents)) return "";
  return (cents / 100).toFixed(2);
}

function isoToDatetimeLocal({ value }: { value?: string }): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function datetimeLocalToIso({ value }: { value: string }): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    throw new Error("Countdown end date is invalid");
  }
  return parsed.toISOString();
}

function contextToState({ context }: { context?: SellAProductTemplateContext }) {
  return {
    productSource: (context?.productId ? "existing" : "new") as ProductSource,
    existingProductId: context?.productId ?? "",
    contextProductId: context?.productId ?? "",
    productName: context?.productName ?? "",
    productMedia: (context?.media ?? []).map((item) => ({
      fileId: item.fileId,
      url: item.url,
      ...(item.video ? { video: true as const } : {}),
    })),
    productPrice: centsToDollarsInput({ cents: context?.price }),
    payWhatYouCan: context?.payWhatYouCan === true,
    countdownText: context?.countdownText ?? "",
    countdownEndDate: isoToDatetimeLocal({ value: context?.countdownEndDate }),
    pageDescription: context?.description ?? "",
  };
}

function normalizeSellAProductState({
  productSource,
  existingProductId,
  contextProductId,
  productName,
  productMedia,
  productPrice,
  payWhatYouCan,
  countdownText,
  countdownEndDate,
  pageDescription,
}: {
  productSource: ProductSource;
  existingProductId: string;
  contextProductId: string;
  productName: string;
  productMedia: ProductMedia[];
  productPrice: string;
  payWhatYouCan: boolean;
  countdownText: string;
  countdownEndDate: string;
  pageDescription: string;
}) {
  return {
    productSource,
    existingProductId: productSource === "existing" ? existingProductId : "",
    contextProductId: productSource === "new" ? contextProductId : "",
    productName: productSource === "new" ? productName.trim() : "",
    productMedia:
      productSource === "new"
        ? productMedia.map((item) => ({
            fileId: item.fileId,
            url: item.url,
            video: item.video === true,
          }))
        : [],
    price:
      productSource === "new"
        ? parsePriceDollarsToCents({ value: productPrice })
        : null,
    payWhatYouCan: productSource === "new" ? payWhatYouCan : false,
    countdownText: countdownText.trim(),
    countdownEndDate,
    pageDescription: pageDescription.trim(),
  };
}

export const SellAProductTemplateFields = forwardRef<
  SellAProductTemplateFieldsHandle,
  {
    context?: SellAProductTemplateContext;
    disabled?: boolean;
    onDirtyChange?: (dirty: boolean) => void;
  }
>(function SellAProductTemplateFields({ context, disabled, onDirtyChange }, ref) {
  const initial = contextToState({ context });
  const [productSource, setProductSource] = useState<ProductSource>(initial.productSource);
  const [products, setProducts] = useState<ProductCatalogOption[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [existingProductId, setExistingProductId] = useState(initial.existingProductId);
  const [contextProductId, setContextProductId] = useState(initial.contextProductId);
  const [productName, setProductName] = useState(initial.productName);
  const [productMedia, setProductMedia] = useState<ProductMedia[]>(initial.productMedia);
  const [productPrice, setProductPrice] = useState(initial.productPrice);
  const [payWhatYouCan, setPayWhatYouCan] = useState(initial.payWhatYouCan);
  const [countdownText, setCountdownText] = useState(initial.countdownText);
  const [countdownEndDate, setCountdownEndDate] = useState(initial.countdownEndDate);
  const [pageDescription, setPageDescription] = useState(initial.pageDescription);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);

  useEffect(() => {
    const next = contextToState({ context });
    setProductSource(next.productSource);
    setExistingProductId(next.existingProductId);
    setContextProductId(next.contextProductId);
    setProductName(next.productName);
    setProductMedia(next.productMedia);
    setProductPrice(next.productPrice);
    setPayWhatYouCan(next.payWhatYouCan);
    setCountdownText(next.countdownText);
    setCountdownEndDate(next.countdownEndDate);
    setPageDescription(next.pageDescription);
  }, [context]);

  useEffect(() => {
    const current = normalizeSellAProductState({
      productSource,
      existingProductId,
      contextProductId,
      productName,
      productMedia,
      productPrice,
      payWhatYouCan,
      countdownText,
      countdownEndDate,
      pageDescription,
    });
    const saved = normalizeSellAProductState(contextToState({ context }));
    onDirtyChange?.(JSON.stringify(current) !== JSON.stringify(saved));
  }, [
    productSource,
    existingProductId,
    contextProductId,
    productName,
    productMedia,
    productPrice,
    payWhatYouCan,
    countdownText,
    countdownEndDate,
    pageDescription,
    context,
    onDirtyChange,
  ]);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingProducts(true);
    fetch("/api/products")
      .then((response) => response.json())
      .then((data) => {
        if (!cancelled) setProducts(data.products || []);
      })
      .catch((error) => {
        console.error("Failed to fetch products:", error);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingProducts(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useImperativeHandle(ref, () => ({
    getPayload: () => {
      const countdownEndDateIso = datetimeLocalToIso({ value: countdownEndDate });
      const pageFields = {
        countdownText: countdownText.trim() || undefined,
        countdownEndDate: countdownEndDateIso,
        description: pageDescription.trim() || undefined,
      };

      if (productSource === "existing") {
        return {
          productId: existingProductId || undefined,
          ...pageFields,
        };
      }

      const parsedPrice = parsePriceDollarsToCents({ value: productPrice });
      return {
        ...(contextProductId ? { productId: contextProductId } : {}),
        product: {
          name: productName.trim(),
          price: parsedPrice ?? 0,
          payWhatYouCan,
          ...(productMedia.length
            ? {
                media: productMedia.map((item) => ({
                  fileId: item.fileId,
                  url: item.url,
                  ...(item.video ? { video: true } : {}),
                })),
              }
            : {}),
        },
        ...pageFields,
      };
    },
    isValid: () => {
      if (productSource === "existing") return Boolean(existingProductId);
      return Boolean(productName.trim() && parsePriceDollarsToCents({ value: productPrice }) !== null);
    },
  }));

  return (
    <>
      <div className="space-y-6">
        <div className="space-y-3">
          <h3 className="text-sm font-medium">Product</h3>
          <Tabs
            value={productSource}
            onValueChange={(value) => setProductSource(value as ProductSource)}
          >
            <TabsList className="w-full">
              <TabsTrigger className="flex-1" value="existing">
                Existing
              </TabsTrigger>
              <TabsTrigger className="flex-1" value="new">
                New
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {productSource === "existing" ? (
            <div className="space-y-2">
              <Label>Select product</Label>
              <Select
                value={existingProductId || undefined}
                onValueChange={setExistingProductId}
                disabled={disabled || isLoadingProducts}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      isLoadingProducts ? "Loading products..." : "Choose a product"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <ProductSelectItems
                    products={products}
                    selectedProductId={existingProductId}
                  />
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="sell-product-name">Name</Label>
                <Input
                  id="sell-product-name"
                  value={productName}
                  onChange={(event) => setProductName(event.target.value)}
                  placeholder="Product name"
                  disabled={disabled}
                />
              </div>

              <div className="space-y-2">
                <Label>Images or Videos</Label>
                <div className="space-y-2">
                  {productMedia.length > 0 ? (
                    <div className="grid grid-cols-3 gap-2">
                      {productMedia.map((item, index) => (
                        <div
                          key={`${item.fileId}-${index}`}
                          className="relative aspect-square overflow-hidden rounded-md bg-muted"
                        >
                          {item.video ? (
                            <video
                              src={item.url}
                              className="h-full w-full object-cover"
                              muted
                              playsInline
                            />
                          ) : (
                            <img
                              src={item.url}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          )}
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute right-1.5 top-1.5 h-6 w-6"
                            onClick={() =>
                              setProductMedia((prev) =>
                                prev.filter((_, i) => i !== index)
                              )
                            }
                            disabled={disabled}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center bg-muted text-muted-foreground">
                      <Video className="h-6 w-6" />
                    </div>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setImagePickerOpen(true)}
                    disabled={disabled}
                  >
                    {productMedia.length > 0 ? "Add image or video" : "Select"}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sell-product-price">Price</Label>
                <Input
                  id="sell-product-price"
                  value={productPrice}
                  onChange={(event) => setProductPrice(event.target.value)}
                  placeholder="0.00"
                  inputMode="decimal"
                  disabled={disabled}
                />
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="sell-pay-what-you-can"
                  checked={payWhatYouCan}
                  onCheckedChange={(checked) => setPayWhatYouCan(checked === true)}
                  disabled={disabled}
                />
                <Label htmlFor="sell-pay-what-you-can" className="cursor-pointer">
                  Pay what you can
                </Label>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-medium">Page</h3>
          <div className="space-y-2">
            <Label htmlFor="sell-countdown-text">Countdown text</Label>
            <Input
              id="sell-countdown-text"
              value={countdownText}
              onChange={(event) => setCountdownText(event.target.value)}
              placeholder="Offer ends in"
              disabled={disabled}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sell-countdown-end">Countdown end date</Label>
            <Input
              id="sell-countdown-end"
              type="datetime-local"
              value={countdownEndDate}
              onChange={(event) => setCountdownEndDate(event.target.value)}
              disabled={disabled}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sell-description">Description</Label>
            <Textarea
              id="sell-description"
              value={pageDescription}
              onChange={(event) => setPageDescription(event.target.value)}
              placeholder="Tell fans about this product"
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      <ImageSelectDialog
        open={imagePickerOpen}
        onOpenChange={setImagePickerOpen}
        onSelect={({ fileId, url, video }) => {
          setProductMedia((prev) => [
            ...prev,
            {
              fileId,
              url,
              ...(video ? { video: true } : {}),
            },
          ]);
          setImagePickerOpen(false);
        }}
        title="Select Image or Video"
        description="Choose an image or video from your uploaded files or upload a new one"
        acceptVideo
      />
    </>
  );
});
